import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import TabList from "@accredo/fe-shared/components/TabList.jsx";
import {
  SearchOutlined,
  FilterListOutlined,
  ViewWeekOutlined,
  ArrowDownwardOutlined,
  MoreVertOutlined,
} from "@mui/icons-material";
import { useState, useEffect, useMemo } from "react";
import { Button, dimensions } from "../theme/index.js";
import components from "../theme/tokens/components.js";
import DataList from "./DataList.jsx";

/**
 * DataTable v2
 * Wrapper around MaterialReactTable styled according to the v2 design system
 *
 * @param {string} variant - Visual variant of the table:
 *   - "enclosed" (default): Border wraps entire component including toolbar
 *   - "compact": Border only around the table, toolbar is outside
 *   - "bare": No border at all, for use inside cards
 */
export default function DataTable({
  columns,
  data,
  localization = MRT_Localization_DE,
  variant = "enclosed", // base, outlined, card
  enableGlobalFilter = true,
  enableColumnFilters = true,
  enableColumnHiding = true,
  enableSorting = true,
  enableHeader = true,
  enableFooter = false,
  initialState,
  manualSorting = true,
  manualFiltering = true,
  renderTopToolbarCustomActions,
  toolbarActions,
  headerStartSlot,
  filterTabs,
  activeFilterTab,
  filterTabsProps,
  title,
  isLoading = false,
  hasLoaded = true,
  // Server-side pagination controls
  pageIndex,
  pageSize,
  totalRows,

  onChangePage,
  onChangeSorting,
  onChangeFilters,
  onChangeGlobalFilter,
  enableTopToolbar = true,

  // Persist user/table preference
  storageKey,
  muiTableBodyRowProps,
  viewMode = null, // 'table' | 'list' | null
  onRowClick,
  rowSelection,
  columnOrder,
  testId = "data-table",
  ...rest
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const borderColor = theme.palette.grey[200];
  const headerBackground = theme.palette.grey[50];

  const footerBorderColor = theme.palette.grey[200];
  const iconColor = theme.palette.text.secondary;

  const [activeBreakpoint, setActiveBreakpoint] = useState(() => {
    return theme.breakpoints.keys.reduce((acc, key) => {
      if (theme.breakpoints.values[key] <= window.innerWidth) {
        return key;
      }
      return acc;
    }, "xs");
  });

  useEffect(() => {
    const handleResize = () => {
      const bp = theme.breakpoints.keys.reduce((acc, key) => {
        if (theme.breakpoints.values[key] <= window.innerWidth) {
          return key;
        }
        return acc;
      }, "xs");
      setActiveBreakpoint(bp);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [theme.breakpoints.keys, theme.breakpoints.values]);

  const __viewMode = useMemo(() => {
    if (viewMode) return viewMode;
    return activeBreakpoint === "xs" || activeBreakpoint === "sm"
      ? "list"
      : "table";
  }, [viewMode, activeBreakpoint]);

  const [sorting, setSorting] = useState(initialState?.sorting || []);
  const [columnFilters, setColumnFilters] = useState(
    initialState?.filters || []
  );
  const [globalFilter, setGlobalFilter] = useState(
    initialState?.globalFilter || ""
  );
  const [expanded, setExpanded] = useState(initialState?.expanded || {});
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);

  // Without pageIndex/pageSize from the caller the table paginates its data
  // client-side and keeps the page state itself.
  const isPaginationControlled =
    typeof pageIndex === "number" && typeof pageSize === "number";
  const [localPagination, setLocalPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const pagination = isPaginationControlled
    ? { pageIndex, pageSize }
    : localPagination;

  // Aufgeklappte Zeilen (Accordion/Detail-Panel) beim Seitenwechsel auf den
  // Initialzustand zurücksetzen (läuft auch beim Mount, daher nicht auf {}
  // sondern auf initialState.expanded, z. B. BookingExportInfoModal)
  useEffect(() => {
    setExpanded(initialState?.expanded || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  const combinedInitialState = {
    density: "comfortable",
    showColumnFilters: false,
    showGlobalFilter: false,
    ...initialState,
  };

  const showInitialLoader = !hasLoaded && isLoading;
  const showOverlayLoader = hasLoaded && isLoading;

  const tablePaperStyles = {
    border: "none",
    borderRadius: 0,
    overflow: "auto",
    backgroundColor: "transparent",
    boxShadow: "none",
  };

  const isBare = variant === "bare";

  return (
    <Box
      data-testid={testId}
      sx={{
        borderRadius: isBare ? 0 : theme.spacing(1),
        border: isBare ? "none" : `1px solid ${borderColor}`,
        overflowX: "auto",
        overflowY: "hidden",
        backgroundColor: isBare
          ? "transparent"
          : theme.palette.background.paper,
      }}
    >
      {/* Title section */}
      {title && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "card.headerBackground",
            px: theme.spacing(2),
            py: theme.spacing(1),
            [theme.breakpoints.up("md")]: {
              px: theme.spacing(4),
              py: theme.spacing(2.5),
            },
          }}
        >
          {typeof title === "string" ? (
            <Typography
              variant="h6"
              sx={{
                fontSize: theme.typography.body2.fontSize,
                fontWeight: 600,
                color: theme.palette.text.primary,
                lineHeight: 1,
                [theme.breakpoints.up("md")]: {
                  fontSize: theme.typography.h6.fontSize,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  lineHeight: 1.5,
                },
              }}
            >
              {title}
            </Typography>
          ) : (
            title
          )}
        </Box>
      )}
      {showInitialLoader ? (
        <SkeletonCard
          titel={title}
          enableHeader={enableHeader}
          viewMode={__viewMode}
          tabCount={filterTabs?.length}
          rowCount={3}
          columns={columns}
          showGlobalFilter={initialState?.showGlobalFilter}
          toolbarActions={toolbarActions}
        />
      ) : (
        <>
          {__viewMode === "list" ? (
            <DataList
              columns={columns}
              data={data}
              onRowClick={onRowClick}
              isLoading={isLoading}
              hasLoaded={hasLoaded}
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRows={totalRows}
              onChangeGlobalFilter={onChangeGlobalFilter}
              onChangePage={onChangePage}
              filterTabs={filterTabs}
              filterTabsProps={filterTabsProps}
              activeFilterTab={activeFilterTab}
              toolbarActions={toolbarActions}
              enableGlobalFilter={enableGlobalFilter}
            />
          ) : (
            <MaterialReactTable
              columns={columns}
              data={data}
              enableColumnResizing={false}
              localization={localization}
              icons={{
                SearchIcon: SearchOutlined,
                FilterListIcon: FilterListOutlined,
                ViewColumnIcon: ViewWeekOutlined,
                ArrowDownwardIcon: ArrowDownwardOutlined,
                MoreVertIcon: MoreVertOutlined,
              }}
              enableFooter={enableFooter}
              enableGlobalFilter={enableGlobalFilter}
              enableColumnFilters={enableColumnFilters}
              enableColumnActions={enableColumnFilters}
              enableColumnOrdering={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={enableColumnHiding}
              enablePagination
              manualSorting={manualSorting}
              enableSorting={enableSorting}
              manualFiltering={manualFiltering}
              manualGlobalFilter={true}
              enableTopToolbar={enableTopToolbar}
              // Was accidentally never applied ("enableBottomToolbar" stuck
              // onto the front, so this was never a real prop name) - MRT's
              // own "X row(s) selected" banner was rendering unsuppressed,
              // duplicating a table's own custom selection UI.
              positionToolbarAlertBanner="none"
              initialState={combinedInitialState}
              enableToolbarInternalActions={true}
              manualPagination={isPaginationControlled}
              rowCount={typeof totalRows === "number" ? totalRows : undefined}
              state={{
                pagination,
                sorting,
                columnFilters,
                globalFilter,
                expanded,
                rowSelection: rowSelection || {},
              }}
              onPaginationChange={(updater) => {
                const current = pagination;
                const next =
                  typeof updater === "function" ? updater(current) : updater;

                if (!isPaginationControlled) {
                  setLocalPagination(next);
                }

                if (
                  storageKey &&
                  typeof window !== "undefined" &&
                  next.pageSize !== current.pageSize
                ) {
                  try {
                    window.localStorage.setItem(
                      storageKey,
                      JSON.stringify({ rows_per_page: next.pageSize })
                    );
                  } catch (e) {
                    /* ignore */
                  }
                }

                if (!onChangePage) {
                  return;
                }
                onChangePage(next);
              }}
              onSortingChange={(updater) => {
                // console.log(updater);
                const current = sorting ?? null;
                const next = updater(current);
                setSorting(next);
                if (!onChangeSorting) {
                  return;
                }
                onChangeSorting(next);
                // console.log(current, next);
              }}
              onColumnFiltersChange={(updater) => {
                const current = columnFilters;
                const next =
                  typeof updater === "function" ? updater(current) : updater;
                setColumnFilters(next);
                if (!onChangeFilters) {
                  return;
                }
                onChangeFilters(next);
              }}
              onGlobalFilterChange={(updater) => {
                const current = globalFilter ?? "";
                const next =
                  typeof updater === "function" ? updater(current) : updater;
                setGlobalFilter(next);
                // MaterialReactTable already debounces its own search field
                // before calling onGlobalFilterChange, so this can call
                // straight through.
                if (!onChangeGlobalFilter) {
                  return;
                }
                onChangeGlobalFilter(next);
              }}
              onExpandedChange={(updater) => {
                const next =
                  typeof updater === "function" ? updater(expanded) : updater;
                setExpanded(next);
              }}
              displayColumnDefOptions={{
                "mrt-row-actions": {
                  header: "Aktionen",
                },
              }}
              muiTablePaperProps={{
                elevation: 0,
                sx: tablePaperStyles,
              }}
              muiTableContainerProps={{
                sx: {
                  overflowX: "auto",
                  overflowY: "visible",
                  borderLeft: 0,
                  borderRight: 0,
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  backgroundColor: headerBackground,
                  borderColor: footerBorderColor,
                  borderRight: "none",
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.body2.fontSize,
                  fontWeight: 600,
                  "&:first-of-type": {
                    paddingLeft: theme.spacing(4),
                  },
                  "&:last-of-type": {
                    borderRight: "none",
                    paddingRight: theme.spacing(4),
                  },
                },
              }}
              muiFilterTextFieldProps={{
                variant: "outlined",
                sx: {
                  "& .MuiInputBase-root": {
                    paddingRight: components.dataTable.filterInput.paddingRight,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: components.dataTable.filterInput.fontSize,
                    paddingTop: components.dataTable.filterInput.paddingY,
                    paddingBottom: components.dataTable.filterInput.paddingY,
                  },
                  "& .MuiInputBase-input::placeholder": {
                    fontSize: components.dataTable.filterInput.fontSize,
                    color: components.dataTable.filterInput.placeholderColor,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: components.dataTable.filterInput.borderColor,
                  },

                  "& .MuiIconButton-root": {
                    padding: components.dataTable.filterInput.icon.padding,
                  },
                  "& .MuiIconButton-root .MuiSvgIcon-root": {
                    fontSize: components.dataTable.filterInput.icon.size,
                  },
                },
              }}
              muiTableHeadRowProps={{
                sx: {
                  "& th": {
                    borderBottomWidth: 2,
                  },
                  display: enableHeader ? "table-row" : "none",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.text.primary,
                  fontSize: theme.typography.body2.fontSize,
                  "&:first-of-type": {
                    paddingLeft: theme.spacing(4),
                  },
                  "&:last-of-type": {
                    paddingRight: theme.spacing(4),
                  },
                },
              }}
              muiTableBodyRowProps={(params) => {
                const customProps =
                  typeof muiTableBodyRowProps === "function"
                    ? muiTableBodyRowProps(params)
                    : muiTableBodyRowProps || {};

                return {
                  ...customProps,
                  onClick: onRowClick
                    ? () => onRowClick(params.row.original)
                    : customProps.onClick,
                  sx: {
                    // `td::after` never painted anything (no pseudo-element
                    // without a `content`) - a clickable row had a pointer
                    // cursor but no visible hover affordance at all.
                    ...(onRowClick && {
                      "&:hover td": {
                        backgroundColor:
                          components.dataTable.row.hoverBackground,
                      },
                    }),
                    cursor: onRowClick ? "pointer" : "default",
                    ...customProps.sx,
                  },
                };
              }}
              muiTableProps={{
                sx: {
                  tableLayout: "fixed",
                  "& .MuiTableBody-root tr:last-of-type td": {
                    borderBottom: "none",
                  },
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  overflow: "visible",
                  px: theme.spacing(1.5),
                  py: theme.spacing(1),
                  borderLeft: 0,
                  borderRight: 0,
                  "& .Mui-ToolbarDropZone+.MuiBox-root": {
                    flexWrap: "wrap",
                    "&>.MuiBox-root+.MuiBox-root": {
                      marginLeft: "auto",
                    },
                  },
                },
              }}
              renderTopToolbarCustomActions={(toolbarProps) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexGrow: 1,
                    gap: theme.spacing(2),
                    // Separates the tabs from MRT's own toolbar icons, so
                    // it's only drawn when at least one of them is shown.
                    borderRight:
                      filterTabs?.length &&
                      (enableGlobalFilter ||
                        enableColumnFilters ||
                        enableColumnHiding)
                        ? `1px solid ${borderColor}`
                        : "none",
                    paddingRight: theme.spacing(2),
                  }}
                >
                  {/* Left side - header start slot OR filter tabs */}
                  {headerStartSlot ? (
                    <Box
                      sx={{
                        marginRight: "auto",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {typeof headerStartSlot === "function"
                        ? headerStartSlot(toolbarProps)
                        : headerStartSlot}
                    </Box>
                  ) : (
                    filterTabs &&
                    filterTabs.length > 0 && (
                      <TabList
                        tabs={filterTabs}
                        activeTab={activeFilterTab}
                        onTabChange={(tabKey) => {
                          const tab = filterTabs.find((t) => t.key === tabKey);
                          tab?.onClick?.(tabKey);
                        }}
                        sx={{ marginRight: "auto" }}
                        containerSx={{
                          width: "auto",
                        }}
                        {...filterTabsProps}
                      />
                    )
                  )}

                  {/* Right side - Custom actions and toolbar icons */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: theme.spacing(2),
                      marginLeft: "auto",
                      flexWrap: "nowrap",
                    }}
                  >
                    {renderTopToolbarCustomActions?.(toolbarProps)}
                    {toolbarActions && toolbarActions.length > 0 && (
                      <>
                        {toolbarActions.map((action, index) => {
                          if (action.type === "menu") {
                            const menuItems = [...(action.items || [])];

                            return (
                              <span key={index}>
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    setActionMenuAnchor(e.currentTarget)
                                  }
                                  sx={{ color: iconColor }}
                                  aria-label={action.label || "Mehr Aktionen"}
                                >
                                  {action.icon || <MoreVertOutlined />}
                                </IconButton>
                                <Menu
                                  anchorEl={actionMenuAnchor}
                                  open={Boolean(actionMenuAnchor)}
                                  onClose={() => setActionMenuAnchor(null)}
                                >
                                  {menuItems.map((item, itemIndex) => (
                                    <MenuItem
                                      key={itemIndex}
                                      disabled={item.disabled}
                                      onClick={() => {
                                        setActionMenuAnchor(null);
                                        item.onClick?.();
                                      }}
                                    >
                                      {item.icon && (
                                        <Box sx={{ mr: 1, display: "flex" }}>
                                          {item.icon}
                                        </Box>
                                      )}
                                      {item.label}
                                    </MenuItem>
                                  ))}
                                </Menu>
                              </span>
                            );
                          }
                          if (action.type === "icon") {
                            return (
                              <IconButton
                                key={index}
                                size="small"
                                onClick={action.onClick}
                                sx={{ color: iconColor }}
                                aria-label={action.label}
                                disabled={action.disabled}
                              >
                                <Tooltip title={action.label}>
                                  {action.icon}
                                </Tooltip>
                              </IconButton>
                            );
                          }
                          if (action.type === "dropdown-button") {
                            return (
                              <DropdownButton
                                label={action.label}
                                icon={action.icon}
                                key={index}
                                options={action.options}
                              />
                            );
                          }
                          return (
                            <Button
                              key={index}
                              variant={action.variant || "contained"}
                              startIcon={action.icon}
                              onClick={action.onClick}
                              size="small"
                              disabled={action.disabled}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                ...action.sx,
                              }}
                            >
                              {action.label}
                            </Button>
                          );
                        })}
                      </>
                    )}
                    {showOverlayLoader && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          ml: theme.spacing(1),
                        }}
                      >
                        <CircularProgress
                          color="primary"
                          size={18}
                          thickness={4}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              muiBottomToolbarProps={{
                sx: {
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  boxShadow: "none",
                  // gap: theme.spacing(3),
                  // minHeight: theme.spacing(6.25),
                  //px: theme.spacing(3),
                  //py: theme.spacing(3),
                  borderTop: `1px solid ${footerBorderColor}`,
                  borderLeft: 0,
                  borderRight: 0,
                  borderBottom: 0,
                  minHeight: "3.125rem",
                  padding: 0,
                  fontSize: theme.typography.caption.fontSize,
                  color: theme.palette.text.primary,
                  ".MuiFormLabel-root": {
                    fontSize: theme.typography.caption.fontSize,
                    color: theme.palette.text.primary,
                  },
                  ".MuiTablePagination-root": {
                    padding: dimensions.sm,
                    fontSize: theme.typography.caption.fontSize,
                    color: theme.palette.text.primary,
                  },
                  ".MuiTablePagination-root .MuiSelect-select": {
                    fontSize: theme.typography.caption.fontSize,
                    color: theme.palette.text.primary,
                    padding: `0 ${dimensions["2xl"]} 0 ${dimensions.lg}`,
                  },
                },
              }}
              muiPaginationProps={{
                rowsPerPageOptions: [5, 10, 25, 50],
                showFirstButton: false,
                showLastButton: false,
                showRowsPerPage: true,
              }}
              // positionActionsColumn="first"
              {...rest}
            />
          )}
        </>
      )}
    </Box>
  );
}

DataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  localization: PropTypes.object,
  variant: PropTypes.oneOf(["enclosed", "compact", "bare"]),
  enableFooter: PropTypes.bool,
  enableGlobalFilter: PropTypes.bool,
  enableColumnFilters: PropTypes.bool,
  enableColumnHiding: PropTypes.bool,
  enableSorting: PropTypes.bool,
  enableHeader: PropTypes.bool,
  initialState: PropTypes.object,
  manualFiltering: PropTypes.bool,
  manualSorting: PropTypes.bool,
  renderTopToolbarCustomActions: PropTypes.func,
  topToolbarIcons: PropTypes.bool,
  toolbarActions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["dropdown-button", "button", "icon", "menu"]),
      label: PropTypes.string,
      icon: PropTypes.node,
      onClick: PropTypes.func,
      variant: PropTypes.string,
      color: PropTypes.string,
      sx: PropTypes.object,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          icon: PropTypes.node,
          onClick: PropTypes.func,
        })
      ),
    })
  ),
  filterTabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
      onClick: PropTypes.func,
    })
  ),
  headerStartSlot: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  activeFilterTab: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  totalRows: PropTypes.number,
  onChangePage: PropTypes.func,
  onChangeSorting: PropTypes.func,
  onChangeFilters: PropTypes.func,
  onChangeGlobalFilter: PropTypes.func,
  storageKey: PropTypes.string,
  muiTableBodyRowProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  isLoading: PropTypes.bool,
  hasLoaded: PropTypes.bool,
  viewMode: PropTypes.oneOf(["table", "list"]),
  onRowClick: PropTypes.func,
};

import {
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import DropdownButton from "./DropdownButton.jsx";

function SkeletonCard({
  title,
  columns,
  rowCount = 3,
  viewMode = "table",
  tabCount = 0,
  enableTopToolbar = true,
  showGlobalFilter = false,
  toolbarActions = [],
}) {
  const theme = useTheme();

  return (
    <Card
      sx={{
        border: 0,
      }}
    >
      {/* Card Header mit grauem Hintergrund und Skeleton-Titel */}
      {title && (
        <Box
          sx={{
            backgroundColor: "card.headerBackground",
            px: theme.spacing(4),
            py: theme.spacing(2.5),
          }}
        >
          <Skeleton variant="text" width="40%" height={30} />
        </Box>
      )}

      {enableTopToolbar && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(2),
            px: theme.spacing(4),
            py: theme.spacing(2),
            justifyContent: "space-between",
          }}
        >
          {tabCount > 0 && (
            <Box sx={{ display: "flex", gap: 2 }}>
              {Array.from({ length: tabCount }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  width={100}
                  height={36}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(2),
              justifyContent: "flex-end",
              marginLeft: "auto",
            }}
          >
            {toolbarActions &&
              toolbarActions.length > 0 &&
              toolbarActions.map((toolbarAction, i) => (
                <Skeleton
                  key={i}
                  variant={
                    toolbarAction.type === "icon" ? "circular" : "rectangular"
                  }
                  width={toolbarAction.type === "icon" ? 32 : 100}
                  height={toolbarAction.type === "icon" ? 32 : 40}
                  sx={{
                    borderRadius: "5px",
                  }}
                />
              ))}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
                justifyContent: "flex-end",
                borderLeft: `1px solid ${theme.palette.grey[200]}`,
                paddingLeft: theme.spacing(2),
              }}
            >
              {showGlobalFilter && (
                <Skeleton
                  variant="rectangular"
                  width={200}
                  height={40}
                  sx={{
                    borderRadius: "5px",
                  }}
                />
              )}

              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </Box>
        </Box>
      )}

      <CardContent
        sx={{
          padding: 0,
        }}
      >
        {viewMode === "list" ? (
          <SkeletonList columns={columns} rowCount={rowCount} />
        ) : (
          <SkeletonTable columns={columns} rowCount={rowCount} />
        )}
      </CardContent>

      <PaginationSkeleton type={viewMode} />
    </Card>
  );
}

function PaginationSkeleton({ type = "list" }) {
  const theme = useTheme();

  if (type === "table") {
    // TablePagination Skeleton (Seiteninfo + Dropdown + Pfeile)
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: theme.spacing(2),
          px: theme.spacing(4),
          py: theme.spacing(2),
        }}
      >
        <Skeleton variant="text" width={60} height={28} />
        <Skeleton variant="rectangular" width={60} height={36} />
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="circular" width={32} height={32} />
      </Box>
    );
  }

  // Standard Pagination Skeleton (z.B. 5 Kreise)
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1.5),
        px: theme.spacing(4),
        py: theme.spacing(2),
      }}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} variant="circular" width={32} height={32} />
      ))}
    </Box>
  );
}

function SkeletonList({ columns, rowCount }) {
  return (
    <List>
      {Array.from({ length: rowCount }).map((_, item) => (
        <ListItem key={item}>
          <ListItemAvatar>
            <Skeleton variant="circular" width={40} height={40} />
          </ListItemAvatar>
          <ListItemText
            primary={<Skeleton variant="text" width="80%" />}
            secondary={<Skeleton variant="text" width="60%" />}
          />
        </ListItem>
      ))}
    </List>
  );
}

function SkeletonTable({ columns = [], rowCount = 3 }) {
  const theme = useTheme();

  const headerBackground = theme.palette.grey[50];
  const footerBorderColor = theme.palette.grey[200];

  return (
    <Table>
      <TableHead>
        <TableRow>
          {columns.map((column, column_index) => (
            <TableCell
              key={column.id || column.accessorKey || column_index}
              sx={{
                backgroundColor: headerBackground,
                borderColor: footerBorderColor,
                borderRight: "none",
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: 600,
                padding: theme.spacing(1),
                "&:first-of-type": {
                  paddingLeft: theme.spacing(4),
                },
                "&:last-of-type": {
                  borderRight: "none",
                  paddingRight: theme.spacing(4),
                },
              }}
            >
              {column.header}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {Array.from({ length: rowCount }).map((_, row) => (
          <TableRow key={row}>
            {columns.map((column, column_index) => (
              <TableCell
                key={`${row}-${column.id || column.accessorKey || column_index}`}
              >
                <Skeleton width="70%" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
